import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectExternalUserDeleting,
  selectExternalUsers,
  selectExternalUsersInvitedByMeOnly,
  selectExternalUsersError,
  selectExternalUsersLoaded,
} from '@/store/selectors/externalUsers';
import { selectUser } from '@/store/selectors/user';
import { resetExternalUsersState } from '@/store/slices/externalUsers/externalUsersSlice';
import { deleteExternalUserThunk } from '@/store/thunks/externalUsers/deleteExternalUserThunk';
import { fetchExternalUsersThunk } from '@/store/thunks/externalUsers/fetchExternalUsersThunk';
import { showSuccessToast } from '@/utils/toast.utils';
import type {
  ExternalUser,
  ExternalUserSortDir,
  ExternalUserSortKey,
  ExternalUserStatusFilter,
} from '@/types/external-users.types';

const ALL_DEPT_ORGS = 'All';

interface UseExternalUsersPageState {
  visibleUsers: ExternalUser[];
  isLoaded: boolean;
  isDeleting: boolean;
  error: string | null;
  searchValue: string;
  sortKey: ExternalUserSortKey;
  sortDir: ExternalUserSortDir;
  deptOrgFilter: string;
  deptOrgOptions: string[];
  statusFilter: ExternalUserStatusFilter;
  pendingDelete: ExternalUser | null;
  showEmptyState: boolean;
  showEmptySearchState: boolean;
}

interface UseExternalUsersPageHandlers {
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  onSortChange: (key: ExternalUserSortKey) => void;
  onDeptOrgFilterChange: (value: string) => void;
  onStatusFilterChange: (value: ExternalUserStatusFilter) => void;
  onDeleteRequest: (user: ExternalUser) => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  onRetry: () => void;
}

interface UseExternalUsersPageReturn {
  state: UseExternalUsersPageState;
  handlers: UseExternalUsersPageHandlers;
}

function getNextSortDir(
  currentKey: ExternalUserSortKey,
  currentDir: ExternalUserSortDir,
  nextKey: ExternalUserSortKey
): ExternalUserSortDir {
  return currentKey === nextKey && currentDir === 'asc' ? 'desc' : 'asc';
}

function compareUsers(
  left: ExternalUser,
  right: ExternalUser,
  sortKey: ExternalUserSortKey
): number {
  if (sortKey === 'sixCount') {
    return left.sixCount - right.sixCount;
  }
  return left[sortKey].localeCompare(right[sortKey], undefined, { sensitivity: 'base' });
}

function matchesSearch(user: ExternalUser, normalizedSearch: string): boolean {
  if (normalizedSearch.length === 0) {
    return true;
  }
  return [user.name, user.email, user.phone, user.deptOrg].some((field) =>
    field.toLowerCase().includes(normalizedSearch)
  );
}

export function useExternalUsersPage(): UseExternalUsersPageReturn {
  const dispatch = useAppDispatch();
  const users = useAppSelector(selectExternalUsers);
  const isLoaded = useAppSelector(selectExternalUsersLoaded);
  const isDeleting = useAppSelector(selectExternalUserDeleting);
  const error = useAppSelector(selectExternalUsersError);
  const invitedByMeOnly = useAppSelector(selectExternalUsersInvitedByMeOnly);
  const currentUserEmail = useAppSelector(selectUser)?.email ?? '';

  const [searchValue, setSearchValue] = useState('');
  const [sortKey, setSortKey] = useState<ExternalUserSortKey>('name');
  const [sortDir, setSortDir] = useState<ExternalUserSortDir>('asc');
  const [deptOrgFilter, setDeptOrgFilter] = useState<string>(ALL_DEPT_ORGS);
  const [statusFilter, setStatusFilter] = useState<ExternalUserStatusFilter>('All');
  const [pendingDelete, setPendingDelete] = useState<ExternalUser | null>(null);

  // Reset on unmount so the sidebar-toggled invitedByMeOnly filter cannot
  // silently persist into the next visit while the local filters start fresh.
  useEffect(() => {
    void dispatch(fetchExternalUsersThunk());
    return () => {
      dispatch(resetExternalUsersState());
    };
  }, [dispatch]);

  const deptOrgOptions = useMemo(() => {
    const unique = new Set(users.map((user) => user.deptOrg).filter((value) => value.length > 0));
    return [ALL_DEPT_ORGS, ...Array.from(unique).sort((a, b) => a.localeCompare(b))];
  }, [users]);

  const visibleUsers = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const normalizedCurrentEmail = currentUserEmail.trim().toLowerCase();
    const filtered = users.filter(
      (user) =>
        matchesSearch(user, normalizedSearch) &&
        (deptOrgFilter === ALL_DEPT_ORGS || user.deptOrg === deptOrgFilter) &&
        (statusFilter === 'All' || user.status === statusFilter) &&
        (!invitedByMeOnly ||
          (normalizedCurrentEmail.length > 0 &&
            user.createdByAccount.toLowerCase() === normalizedCurrentEmail))
    );
    const sorted = [...filtered].sort((left, right) => compareUsers(left, right, sortKey));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [
    users,
    searchValue,
    deptOrgFilter,
    statusFilter,
    sortKey,
    sortDir,
    invitedByMeOnly,
    currentUserEmail,
  ]);

  const onSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const onSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  const onSortChange = useCallback(
    (key: ExternalUserSortKey) => {
      setSortDir(getNextSortDir(sortKey, sortDir, key));
      setSortKey(key);
    },
    [sortKey, sortDir]
  );

  const onDeptOrgFilterChange = useCallback((value: string) => {
    setDeptOrgFilter(value);
  }, []);

  const onStatusFilterChange = useCallback((value: ExternalUserStatusFilter) => {
    setStatusFilter(value);
  }, []);

  const onDeleteRequest = useCallback((user: ExternalUser) => {
    setPendingDelete(user);
  }, []);

  const onDeleteCancel = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const onDeleteConfirm = useCallback(() => {
    if (pendingDelete === null || isDeleting) {
      return;
    }
    void dispatch(deleteExternalUserThunk(pendingDelete.id)).then((action) => {
      if (deleteExternalUserThunk.fulfilled.match(action)) {
        showSuccessToast('External user deleted');
      }
      setPendingDelete(null);
    });
  }, [dispatch, pendingDelete, isDeleting]);

  const onRetry = useCallback(() => {
    void dispatch(fetchExternalUsersThunk());
  }, [dispatch]);

  return {
    state: {
      visibleUsers,
      isLoaded,
      isDeleting,
      error,
      searchValue,
      sortKey,
      sortDir,
      deptOrgFilter,
      deptOrgOptions,
      statusFilter,
      pendingDelete,
      showEmptyState: isLoaded && users.length === 0,
      showEmptySearchState: users.length > 0 && visibleUsers.length === 0,
    },
    handlers: {
      onSearchChange,
      onSearchClear,
      onSortChange,
      onDeptOrgFilterChange,
      onStatusFilterChange,
      onDeleteRequest,
      onDeleteConfirm,
      onDeleteCancel,
      onRetry,
    },
  };
}
